/**
 * Ende-zu-Ende auf dem Gerät (E21, 25.10.2026) — der Kontoschlüssel und was
 * mit ihm geschieht.
 *
 * - **Kontoschlüssel:** 32 Zufallsbytes, AES-GCM. Er verschlüsselt die
 *   Inhaltsfelder (`sealRow`/`openRow`). Auf dem Gerät liegt er in der lokalen
 *   Datenbank (`meta`) — dort liegt der Klartext ohnehin; Abmelden löscht ihn mit.
 * - **Verpackt** verlässt er das Gerät: je Passkey mit dem PRF-Wert (am iPhone
 *   gemessen: in Safari und als App stabil) und einmal mit dem
 *   Wiederherstellungscode. Aus beidem wird per HKDF ein Packschlüssel.
 *
 * Nichts hiervon kennt der Server. Verliert jemand Passkey **und** Code, sind
 * die verschlüsselten Inhalte weg — das sagt die Oberfläche beim Einrichten.
 */

import { E2E_PAYLOAD_PREFIX, SEALED_EMPTY, SEALED_FILE_MAGIC } from '@kraftwerk/shared';
import type { EncryptedTable } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';

// --- Base64url ------------------------------------------------------------------

export function toB64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

export function randomBytes(length: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(length));
}

// --- Schlüssel --------------------------------------------------------------------

export function newAccountKey(): Uint8Array<ArrayBuffer> {
  return randomBytes(32);
}

function importAesKey(raw: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

/** Packschlüssel aus einem Geheimnis (PRF-Wert oder Code) — HKDF-SHA-256. */
async function wrapKeyFrom(
  secret: Uint8Array<ArrayBuffer>,
  salt: Uint8Array<ArrayBuffer>,
  purpose: 'passkey' | 'recovery' | 'household',
): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', secret, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt,
      info: new TextEncoder().encode(`kraftwerk-e2e-${purpose}`),
    },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function seal(key: CryptoKey, plain: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const iv = randomBytes(12);
  const cipher = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain));
  const out = new Uint8Array(iv.length + cipher.length);
  out.set(iv);
  out.set(cipher, iv.length);
  return out;
}

async function open(key: CryptoKey, sealed: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: sealed.slice(0, 12) },
      key,
      sealed.slice(12),
    ),
  );
}

export interface Wrap {
  salt: string;
  wrapped: string;
}

/** Den Kontoschlüssel verpacken — mit PRF-Wert oder Code. */
export async function wrapAccountKey(
  accountKey: Uint8Array<ArrayBuffer>,
  secret: Uint8Array<ArrayBuffer>,
  purpose: 'passkey' | 'recovery',
  salt: Uint8Array<ArrayBuffer> = randomBytes(32),
): Promise<Wrap> {
  const wrapKey = await wrapKeyFrom(secret, salt, purpose);
  return { salt: toB64(salt), wrapped: toB64(await seal(wrapKey, accountKey)) };
}

/** Auspacken — wirft, wenn Geheimnis oder Verpackung nicht passen. */
export async function unwrapAccountKey(
  wrap: Wrap,
  secret: Uint8Array<ArrayBuffer>,
  purpose: 'passkey' | 'recovery',
): Promise<Uint8Array<ArrayBuffer>> {
  const wrapKey = await wrapKeyFrom(secret, fromB64(wrap.salt), purpose);
  return new Uint8Array(await open(wrapKey, fromB64(wrap.wrapped)));
}

// --- Wiederherstellungscode -------------------------------------------------------

/** Ohne 0/O, 1/I/L, U — beim Abtippen nicht zu verwechseln (Crockford). */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** 20 Zufallsbytes als 32 Zeichen in Achtergruppen: `ABCD-EFGH-…`. */
export function newRecoveryCode(): string {
  const bytes = randomBytes(20);
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  return out.match(/.{4}/g)!.join('-');
}

/**
 * Code zurück in Bytes — verzeiht Kleinschreibung, Leerzeichen, Bindestriche
 * und die üblichen Verwechsler (O→0, I/L→1). `null`, wenn es kein Code ist.
 */
export function parseRecoveryCode(input: string): Uint8Array<ArrayBuffer> | null {
  const clean = input.toUpperCase().replace(/[\s-]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  if (clean.length !== 32) return null;
  const bytes = new Uint8Array(20);
  let bits = 0;
  let value = 0;
  let index = 0;
  for (const char of clean) {
    const digit = ALPHABET.indexOf(char);
    if (digit < 0) return null;
    value = (value << 5) | digit;
    bits += 5;
    if (bits >= 8) {
      bytes[index++] = (value >>> (bits - 8)) & 255;
      bits -= 8;
    }
  }
  return bytes;
}

// --- Auf dem Gerät ----------------------------------------------------------------

/** Wo der Kontoschlüssel liegt — auch für Live-Abfragen, die nur nachsehen, ob er da ist. */
export const KEY_META = 'e2eKey';
const SETUP_META = 'e2eSetup';

let cached: { raw: string; key: CryptoKey } | null = null;

/** Der Kontoschlüssel dieses Geräts — `null`, solange es nicht entsperrt ist. */
export async function readAccountKey(database: KraftwerkDatabase = db): Promise<CryptoKey | null> {
  const raw = (await database.meta.get(KEY_META))?.value;
  if (!raw) return null;
  if (cached?.raw !== raw) cached = { raw, key: await importAesKey(fromB64(raw)) };
  return cached.key;
}

/** Roh, zum Verpacken für einen weiteren Passkey — `null`, wenn nicht entsperrt. */
export async function readRawAccountKey(
  database: KraftwerkDatabase = db,
): Promise<Uint8Array<ArrayBuffer> | null> {
  const raw = (await database.meta.get(KEY_META))?.value;
  return raw ? fromB64(raw) : null;
}

export async function storeAccountKey(
  raw: Uint8Array<ArrayBuffer>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.bulkPut([
    { key: KEY_META, value: toB64(raw) },
    { key: SETUP_META, value: 'ja' },
  ]);
}

/** Hat das Konto (laut letztem Abgleich) einen Kontoschlüssel? */
export async function readEncryptionSetup(database: KraftwerkDatabase = db): Promise<boolean> {
  return (await database.meta.get(SETUP_META))?.value === 'ja';
}

export async function writeEncryptionSetup(
  on: boolean,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: SETUP_META, value: on ? 'ja' : 'nein' });
}

const UNLOCKED_BY_META = 'e2eUnlockedBy';

/**
 * Womit dieses Gerät den Schlüssel hat: `passkey` (Einrichten oder Face ID)
 * oder `code`. Nur nach dem Code fehlt hier Face ID — dann bietet die
 * Oberfläche an, sie hinzuzufügen (25.10.2026: der Knopf stand sonst auch auf
 * dem Gerät, das gerade mit Face ID eingerichtet hatte, und tat sichtbar nichts).
 */
export type UnlockedBy = 'passkey' | 'code';

export async function readUnlockedBy(database: KraftwerkDatabase = db): Promise<UnlockedBy | null> {
  const value = (await database.meta.get(UNLOCKED_BY_META))?.value;
  return value === 'passkey' || value === 'code' ? value : null;
}

export async function writeUnlockedBy(
  value: UnlockedBy,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: UNLOCKED_BY_META, value });
}

export type EncryptionState = 'off' | 'locked' | 'ready';

export async function readEncryptionState(
  database: KraftwerkDatabase = db,
): Promise<EncryptionState> {
  const [key, setup] = await Promise.all([
    database.meta.get(KEY_META),
    database.meta.get(SETUP_META),
  ]);
  if (key?.value) return 'ready';
  return setup?.value === 'ja' ? 'locked' : 'off';
}

// --- Dateien ----------------------------------------------------------------------

/** Bild verschlüsseln: `KWE1` ‖ IV ‖ AES-GCM — der Server erkennt nur den Kopf. */
export async function sealFile(
  plain: Uint8Array<ArrayBuffer>,
  key: CryptoKey,
): Promise<Uint8Array> {
  const body = await seal(key, plain);
  const out = new Uint8Array(SEALED_FILE_MAGIC.length + body.length);
  out.set(SEALED_FILE_MAGIC);
  out.set(body, SEALED_FILE_MAGIC.length);
  return out;
}

export async function openFile(
  sealed: Uint8Array<ArrayBuffer>,
  key: CryptoKey,
): Promise<Uint8Array> {
  return open(key, sealed.slice(SEALED_FILE_MAGIC.length));
}

// --- Zeilen -----------------------------------------------------------------------

type Row = Record<string, unknown>;

/** Inhaltsfelder ins Chiffrat, im Umschlag nur Leeres. */
export async function sealRow(table: EncryptedTable, row: Row, key: CryptoKey): Promise<Row> {
  const empty = SEALED_EMPTY[table] as Row;
  const secret: Row = {};
  for (const field of Object.keys(empty)) secret[field] = row[field] ?? empty[field];
  const plain = new TextEncoder().encode(JSON.stringify(secret));
  const payload = E2E_PAYLOAD_PREFIX + toB64(await seal(key, plain));
  return { ...row, ...empty, payload };
}

/** Chiffrat öffnen — wirft bei falschem Schlüssel oder beschädigtem Inhalt. */
export async function openRow(table: EncryptedTable, row: Row, key: CryptoKey): Promise<Row> {
  const payload = row.payload;
  if (typeof payload !== 'string' || !payload.startsWith(E2E_PAYLOAD_PREFIX)) return row;
  const plain = await open(key, fromB64(payload.slice(E2E_PAYLOAD_PREFIX.length)));
  const secret = JSON.parse(new TextDecoder().decode(plain)) as Row;
  const fields: Row = {};
  for (const field of Object.keys(SEALED_EMPTY[table])) fields[field] = secret[field];
  const { payload: _chiffrat, ...envelope } = row;
  return { ...envelope, ...fields };
}

/**
 * Mitteilung (Standort, 27.10.2026) verschlüsseln — Format wie ein Chiffrat
 * (`kwe1.` + Base64(IV ‖ AES-GCM)); der Service Worker öffnet sie mit dem
 * Haushaltsschlüssel (`push/sealedNotice.ts`).
 */
export async function sealNotice(notice: unknown, key: CryptoKey): Promise<string> {
  const plain = new TextEncoder().encode(JSON.stringify(notice));
  return E2E_PAYLOAD_PREFIX + toB64(await seal(key, plain));
}

export function isSealed(row: Row): boolean {
  return typeof row.payload === 'string' && row.payload.startsWith(E2E_PAYLOAD_PREFIX);
}

// --- Haushalt (Schlüsselpaar und Haushaltsschlüssel) ------------------------------

const IDENTITY_META = 'e2eIdentity';
const HOUSEHOLDS_META = 'e2eHouseholds';
const householdMeta = (id: string) => `hk:${id}`;

export interface Identity {
  /** Öffentlich, roh (65 Byte), base64url — für die anderen im Haushalt. */
  publicKey: string;
  /** Privat, PKCS#8 — bleibt auf dem Gerät; zum Server nur verpackt. */
  privatePkcs8: Uint8Array<ArrayBuffer>;
}

export async function newIdentity(): Promise<Identity> {
  const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ]);
  const publicKey = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
  const privatePkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
  return { publicKey: toB64(publicKey), privatePkcs8 };
}

/** Den privaten Schlüssel mit dem Kontoschlüssel verpacken (für den Server). */
export async function sealWithAccountKey(
  plain: Uint8Array<ArrayBuffer>,
  key: CryptoKey,
): Promise<string> {
  return toB64(await seal(key, plain));
}

export async function openWithAccountKey(
  sealed: string,
  key: CryptoKey,
): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await open(key, fromB64(sealed)));
}

export async function storeIdentity(
  privatePkcs8: Uint8Array<ArrayBuffer>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: IDENTITY_META, value: toB64(privatePkcs8) });
}

export async function readIdentity(
  database: KraftwerkDatabase = db,
): Promise<Uint8Array<ArrayBuffer> | null> {
  const value = (await database.meta.get(IDENTITY_META))?.value;
  return value ? fromB64(value) : null;
}

/** Packschlüssel aus ECDH(meins, deins), an den Haushalt gebunden. */
async function householdWrapKey(
  privateKey: CryptoKey,
  publicRaw: Uint8Array<ArrayBuffer>,
  householdId: string,
): Promise<CryptoKey> {
  const peer = await crypto.subtle.importKey(
    'raw',
    publicRaw,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    [],
  );
  const bits = new Uint8Array(
    await crypto.subtle.deriveBits({ name: 'ECDH', public: peer }, privateKey, 256),
  );
  return wrapKeyFrom(bits, new TextEncoder().encode(householdId), 'household');
}

/** Haushaltsschlüssel für ein Mitglied verpacken: Einmal-Schlüssel ‖ IV ‖ Chiffrat. */
export async function wrapHouseholdKey(
  householdKey: Uint8Array<ArrayBuffer>,
  memberPublicKey: string,
  householdId: string,
): Promise<string> {
  const ephemeral = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, [
    'deriveBits',
  ]);
  const wrapKey = await householdWrapKey(
    ephemeral.privateKey,
    fromB64(memberPublicKey),
    householdId,
  );
  const ephemeralRaw = new Uint8Array(await crypto.subtle.exportKey('raw', ephemeral.publicKey));
  const body = await seal(wrapKey, householdKey);
  const out = new Uint8Array(ephemeralRaw.length + body.length);
  out.set(ephemeralRaw);
  out.set(body, ephemeralRaw.length);
  return toB64(out);
}

export async function unwrapHouseholdKey(
  wrapped: string,
  privatePkcs8: Uint8Array<ArrayBuffer>,
  householdId: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const bytes = fromB64(wrapped);
  const privateKey = await crypto.subtle.importKey(
    'pkcs8',
    privatePkcs8,
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits'],
  );
  const wrapKey = await householdWrapKey(privateKey, bytes.slice(0, 65), householdId);
  return new Uint8Array(await open(wrapKey, bytes.slice(65)));
}

export async function storeHouseholdKey(
  householdId: string,
  raw: Uint8Array<ArrayBuffer>,
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: householdMeta(householdId), value: toB64(raw) });
}

const generationMeta = (id: string) => `hkgen:${id}`;

/** Welche Generation des Haushaltsschlüssels dieses Gerät hat (vor dem 26.10.2026: 1). */
export async function readHouseholdGeneration(
  householdId: string,
  database: KraftwerkDatabase = db,
): Promise<number> {
  return Number((await database.meta.get(generationMeta(householdId)))?.value ?? 1);
}

/**
 * Neuer Haushaltsschlüssel nach einem Austritt: Der alte bleibt als
 * `hk:<Haushalt>~<Generation>` liegen — Älteres beim Server und in der
 * Outbox ist noch damit verschlüsselt und öffnet über `knownKeys`.
 */
export async function replaceHouseholdKey(
  householdId: string,
  raw: Uint8Array<ArrayBuffer>,
  generation: number,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const previous = (await database.meta.get(householdMeta(householdId)))?.value;
  const previousGeneration = await readHouseholdGeneration(householdId, database);
  const entries = [
    { key: householdMeta(householdId), value: toB64(raw) },
    { key: generationMeta(householdId), value: String(generation) },
  ];
  if (previous && previous !== toB64(raw)) {
    entries.push({ key: householdMeta(`${householdId}~${previousGeneration}`), value: previous });
  }
  await database.meta.bulkPut(entries);
}

const householdCache = new Map<string, { raw: string; key: CryptoKey }>();

export async function readHouseholdKey(
  householdId: string,
  database: KraftwerkDatabase = db,
): Promise<CryptoKey | null> {
  const raw = (await database.meta.get(householdMeta(householdId)))?.value;
  if (!raw) return null;
  const hit = householdCache.get(householdId);
  if (hit?.raw === raw) return hit.key;
  const key = await importAesKey(fromB64(raw));
  householdCache.set(householdId, { raw, key });
  return key;
}

/** Verschlüsselte Haushalte laut letztem Abgleich. */
export async function readEncryptedHouseholds(database: KraftwerkDatabase = db): Promise<string[]> {
  const value = (await database.meta.get(HOUSEHOLDS_META))?.value;
  try {
    return value ? (JSON.parse(value) as string[]) : [];
  } catch {
    return [];
  }
}

export async function writeEncryptedHouseholds(
  ids: readonly string[],
  database: KraftwerkDatabase = db,
): Promise<void> {
  await database.meta.put({ key: HOUSEHOLDS_META, value: JSON.stringify([...ids].sort()) });
}

/**
 * Welcher Schlüssel für diese Zeile gilt: geteilt der Haushaltsschlüssel,
 * sonst der Kontoschlüssel. `hold`: verschlüsselt, aber dieses Gerät hat den
 * Schlüssel (noch) nicht — nicht als Klartext senden, nicht anzeigen.
 */
export async function keyForRow(
  row: Record<string, unknown>,
  database: KraftwerkDatabase = db,
): Promise<{ key: CryptoKey | null; hold: boolean }> {
  const householdId = typeof row.householdId === 'string' ? row.householdId : null;
  if (householdId) {
    const key = await readHouseholdKey(householdId, database);
    if (key) return { key, hold: false };
    return { key: null, hold: (await readEncryptedHouseholds(database)).includes(householdId) };
  }
  const key = await readAccountKey(database);
  if (key) return { key, hold: false };
  return { key: null, hold: await readEncryptionSetup(database) };
}

/**
 * Jeder Schlüssel, den das Gerät kennt: Kontoschlüssel und alle
 * Haushaltsschlüssel. Für Chiffrat, dessen Schlüssel nicht feststeht — ein
 * umgezogener Haushalt, ein geteiltes Rezeptbild.
 */
export async function knownKeys(database: KraftwerkDatabase = db): Promise<CryptoKey[]> {
  const keys: CryptoKey[] = [];
  const account = await readAccountKey(database);
  if (account) keys.push(account);
  const households = await database.meta.where('key').startsWith('hk:').primaryKeys();
  for (const metaKey of households) {
    const key = await readHouseholdKey(String(metaKey).slice(3), database);
    if (key) keys.push(key);
  }
  return keys;
}
